import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { updateUser } = useAuth();
    const hasProcessed = useRef(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Use useRef to prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            try {
                // Extract session_id from URL hash
                const hash = location.hash;
                const sessionIdMatch = hash.match(/session_id=([^&]+)/);
                
                if (!sessionIdMatch) {
                    console.error('No session_id found in URL');
                    setError('No session ID found. Please try logging in again.');
                    setTimeout(() => navigate('/'), 2000);
                    return;
                }

                const sessionId = sessionIdMatch[1];

                // Exchange session_id for session_token
                const response = await authAPI.exchangeSession(sessionId);
                
                if (response.data?.user) {
                    updateUser(response.data.user);
                    // Navigate to dashboard with user data to skip auth check
                    navigate('/dashboard', { state: { user: response.data.user }, replace: true });
                } else {
                    console.error('No user data in response');
                    setError('Authentication failed. Please try again.');
                    setTimeout(() => navigate('/'), 2000);
                }
            } catch (error) {
                console.error('Auth callback error:', error);
                setError('Authentication failed. Please try again.');
                setTimeout(() => navigate('/'), 2000);
            }
        };

        processAuth();
    }, [location, navigate, updateUser]);

    return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center">
            <div className="scanlines" />
            <div className="terminal-window p-8 text-center">
                <div className="matrix-loader mx-auto mb-4" />
                {error ? (
                    <>
                        <p className="text-[#ff0033] font-mono">
                            {'>'} ERROR: {error}
                        </p>
                        <p className="text-[#008f11] text-sm font-mono mt-2">
                            Redirecting to login...
                        </p>
                    </>
                ) : (
                    <>
                        <p className="text-[#00ff41] font-mono">
                            {'>'} AUTHENTICATING...
                        </p>
                        <p className="text-[#008f11] text-sm font-mono mt-2">
                            Establishing secure connection
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthCallback;
